import React, { useEffect, useMemo, useState } from 'react';

import {
	type ICaptureState,
	type IMergedSlot,
	PU_EVENT_HINTS,
	PU_EVENT_LABELS,
	type PuEventType,
} from '../../../interface/pu-events';
import { deriveIntervalDeltas, deriveScheduleVariance, formatAgo } from '../../../lib/duration';
import { DEFAULT_INEC_SCHEDULE, getWindow } from '../../../lib/election-day-schedule';
import { CheckSVG, ClockSVG } from '../../utils/svgs';
import CaptureButton from './capture-button';
import PhotoAttach from './photo-attach';

interface IPulseTimelineProps {
	slots: IMergedSlot[];
	nextExpected: PuEventType | null;
	nextExpectedAt: string | null;
	overdue: PuEventType[];
	captureState: ICaptureState;
	onCapture: (eventType: PuEventType) => void;
	onUndo: () => void;
	onRetry: () => void;
	onDismiss: () => void;
	/** Re-send everything still queued on the device (post-reload failures). */
	onFlush: () => void;
	onAttachPhoto: (slot: IMergedSlot, file: File, onProgress?: (percent: number) => void) => Promise<boolean>;
	/** The unit is cancelled: show the record, offer nothing to capture or attach. */
	paused?: boolean;
}

const timeOf = (iso: string | null): string => {
	if (!iso) return '';
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) return '';
	return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const gpsChip = (slot: IMergedSlot): string | null => {
	const lat = Number(slot.location?.latitude);
	const lng = Number(slot.location?.longitude);
	if (!lat && !lng) return null;
	if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
	return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
};

const isCapturedLike = (slot: IMergedSlot): boolean => slot.status === 'captured' || slot.status === 'syncing';

/** The most recent captured/syncing slot by occurredAt — anchors the live "x min ago" line. */
const latestCaptured = (slots: IMergedSlot[]): IMergedSlot | null => {
	let latest: IMergedSlot | null = null;
	for (const slot of slots) {
		if (!isCapturedLike(slot) || !slot.occurredAt) continue;
		if (!latest || new Date(slot.occurredAt).getTime() > new Date(latest.occurredAt!).getTime()) {
			latest = slot;
		}
	}
	return latest;
};

/**
 * The 8-slot vertical pulse timeline. The "next" slot carries the primary
 * capture CTA; other uncaptured slots keep a quiet secondary affordance
 * (out-of-order capture is allowed — election day is messy).
 */
const PulseTimeline = ({
	slots,
	nextExpected,
	nextExpectedAt,
	overdue,
	captureState,
	onCapture,
	onUndo,
	onRetry,
	onDismiss,
	onFlush,
	onAttachPhoto,
	paused = false,
}: IPulseTimelineProps) => {
	// Single 30s ticker — sole consumer is the "x min ago" line on the next slot.
	const [now, setNow] = useState(() => new Date());
	useEffect(() => {
		const id = window.setInterval(() => setNow(new Date()), 30_000);
		return () => window.clearInterval(id);
	}, []);

	const deltas = useMemo(() => deriveIntervalDeltas(slots), [slots]);
	const lastCaptured = useMemo(() => latestCaptured(slots), [slots]);
	const lastCapturedAgo = lastCaptured ? formatAgo(lastCaptured.occurredAt, now) : null;

	return (
		<ol className='pue-timeline'>
			{slots.map((slot, index) => {
				const isNext = slot.eventType === nextExpected && slot.status === 'pending';
				const isOverdue = overdue.includes(slot.eventType) && slot.status === 'pending';
				const machineActiveHere = captureState.eventType === slot.eventType && captureState.phase !== 'idle';
				const visual = slot.status === 'pending' && isNext ? 'next' : slot.status;
				const coords = gpsChip(slot);
				const delta = deltas.get(slot.eventType);
				const variance = isCapturedLike(slot)
					? deriveScheduleVariance(slot.occurredAt, getWindow(DEFAULT_INEC_SCHEDULE, slot.eventType)?.expectedHHMM ?? null)
					: null;

				return (
					<li
						key={slot.eventType}
						className={`pue-timeline__item pue-timeline__item--${visual} ${isOverdue ? 'pue-timeline__item--overdue' : ''}`}
					>
						<span
							className='pue-timeline__rail'
							data-last={index === slots.length - 1 || undefined}
							aria-hidden='true'
						/>
						{delta ? (
							<span
								className={`pue-timeline__delta${delta.outOfOrder ? ' pue-timeline__delta--ooo' : ''}${
									delta.spansGap ? ' pue-timeline__delta--gap' : ''
								}`}
								title={`${delta.label} ${delta.outOfOrder ? 'before' : 'after'} ${PU_EVENT_LABELS[delta.fromEventType]}${
									delta.outOfOrder ? ' — captured out of order' : ''
								}`}
								aria-label={`${delta.label} ${delta.outOfOrder ? 'before' : 'after'} ${PU_EVENT_LABELS[delta.fromEventType]}`}
							>
								{delta.outOfOrder ? '⇅ ' : ''}
								{delta.label}
							</span>
						) : null}
						<span
							className='pue-timeline__dot'
							aria-hidden='true'
						>
							{slot.status === 'captured' || slot.status === 'syncing' ? (
								<CheckSVG
									width='12'
									height='12'
									color='currentColor'
								/>
							) : (
								<span className='pue-timeline__dot-index'>{index + 1}</span>
							)}
						</span>

						<div className='pue-timeline__body'>
							<div className='pue-timeline__head'>
								<p className='pue-timeline__label'>{slot.label}</p>
								{slot.status === 'captured' || slot.status === 'syncing' ? (
									<span className='pue-timeline__time'>
										<ClockSVG
											width='12'
											height='12'
											color='currentColor'
										/>
										{timeOf(slot.occurredAt)}
									</span>
								) : null}
							</div>

							{slot.status === 'syncing' ? (
								<span className='pue-timeline__chip pue-timeline__chip--syncing'>Syncing…</span>
							) : null}
							{slot.status === 'failed' && !machineActiveHere ? (
								<span className='pue-timeline__chip pue-timeline__chip--failed'>Not sent — kept on this device</span>
							) : null}
							{isOverdue ? <span className='pue-timeline__chip pue-timeline__chip--overdue'>Overdue</span> : null}
							{slot.gpsDegraded ? <span className='pue-timeline__chip pue-timeline__chip--gps'>No GPS at capture</span> : null}
							{variance ? (
								<span
									className={`pue-timeline__chip pue-timeline__chip--var-${variance.tone}`}
									title={`Expected ~${getWindow(DEFAULT_INEC_SCHEDULE, slot.eventType)?.expectedHHMM}`}
								>
									{variance.label}
								</span>
							) : null}

							{coords && (slot.status === 'captured' || slot.status === 'syncing') ? (
								<p className='pue-timeline__gps'>{coords}</p>
							) : null}

							{slot.mediaUrls.length > 0 ? (
								<div className='pue-timeline__photos'>
									{slot.mediaUrls.map((url) => (
										<a
											key={url}
											href={url}
											target='_blank'
											rel='noreferrer'
											className='pue-timeline__photo'
										>
											<img
												src={url}
												alt={`${slot.label} evidence`}
												loading='lazy'
											/>
										</a>
									))}
								</div>
							) : null}

							{slot.status === 'captured' && !paused ? (
								<PhotoAttach
									slot={slot}
									onAttach={onAttachPhoto}
								/>
							) : null}

							{!paused && (isNext || machineActiveHere) ? (
								<>
									<p className='pue-timeline__hint'>
										{PU_EVENT_HINTS[slot.eventType]}
										{isNext && nextExpectedAt ? ` (expected ~${nextExpectedAt})` : ''}
									</p>
									{isNext && lastCaptured && lastCapturedAgo ? (
										<p className='pue-timeline__since'>
											{lastCaptured.label} was {lastCapturedAgo}
										</p>
									) : null}
									<CaptureButton
										eventType={slot.eventType}
										captureState={captureState}
										onTap={onCapture}
										onUndo={onUndo}
										onRetry={onRetry}
										onDismiss={onDismiss}
									/>
								</>
							) : null}

							{!paused && !isNext && !machineActiveHere && slot.status === 'pending' ? (
								<button
									type='button'
									className='pue-timeline__capture-quiet'
									onClick={() => onCapture(slot.eventType)}
									disabled={captureState.phase !== 'idle'}
								>
									Capture now
								</button>
							) : null}

							{!isNext && !machineActiveHere && slot.status === 'failed' ? (
								<button
									type='button'
									className='pue-timeline__capture-quiet'
									onClick={onFlush}
									disabled={captureState.phase !== 'idle'}
								>
									Retry send
								</button>
							) : null}
						</div>
					</li>
				);
			})}
		</ol>
	);
};

export default PulseTimeline;
